<script setup>
import { EditorContent } from '@tiptap/vue-3'
import TipTapToolbar from '@/tiptap/components/TiptapToolbar.vue'
import BubbleMenu from '@/tiptap/components/BubbleMenu.vue'
import OriginalThemeProvider from '@/tiptap/extensions/components/OriginalThemeProvider.vue'
import '@/tiptap/styles/index.scss'

defineProps({ editor: { type: Object, required: true } })
</script>

<template>
  <div class="vuetify-pro-tiptap">
    <OriginalThemeProvider>
      <VInput class="pt-0" hide-details="auto">
        <VCard
          flat
          outlined
          color="grey-lighten-4"
          class="vuetify-pro-tiptap-editor"
          style="width: 100%; --tiptap-toolbar-offset: var(--v-layout-top, 0px)"
        >
          <!-- Toolbar -->
          <div class="tiptap-toolbar-sticky">
            <TipTapToolbar
              v-if="editor"
              :editor="editor"
              class="vuetify-pro-tiptap-editor__toolbar"
            />
          </div>

          <!-- Edit content -->
          <EditorContent
            v-if="editor"
            :editor="editor"
            class="vuetify-pro-tiptap-editor__content markdown-theme-github"
          />

          <!-- Bubble menu -->
          <BubbleMenu v-if="editor" :editor="editor" />
        </VCard>
      </VInput>
    </OriginalThemeProvider>
  </div>
</template>

<style scoped>

/* Disable clicking on handbook links in edit mode */
:deep(.vuetify-pro-tiptap-editor__content a.mention-link),
:deep(.vuetify-pro-tiptap-editor__content a.mention),
:deep(.vuetify-pro-tiptap-editor__content a[href^="/handbook/article/"]) {
  pointer-events: none !important;
  cursor: text !important;
  text-decoration: underline !important;
  color: #1f6feb !important;
}

/* Deleted handbook refs in editor: link-like, non-clickable, subtle strike-through. */
:deep(.vuetify-pro-tiptap-editor__content .mention-deleted),
:deep(.vuetify-pro-tiptap-editor__content .deleted-link),
:deep(.vuetify-pro-tiptap-editor__content span.deleted-link),
:deep(.vuetify-pro-tiptap-editor__content span.mention-deleted),
:deep(.vuetify-pro-tiptap-editor__content a[title*="Deleted article"]),
:deep(.vuetify-pro-tiptap-editor__content span[title*="Deleted article"]),
:deep(.vuetify-pro-tiptap-editor__content [data-deleted-article-id]) {
  color: #1f6feb !important;
  text-decoration: underline line-through !important;
  text-decoration-color: #94a3b8 !important;
  text-decoration-thickness: 1px;
  pointer-events: none !important;
  cursor: not-allowed !important;
  background: transparent !important;
  border: 0 !important;
  box-shadow: none !important;
}

/* Allow sticky to escape Vuetify wrappers */
:deep(.v-input),
:deep(.v-input .v-field),
:deep(.v-card.vuetify-pro-tiptap-editor),
:deep(.v-card-text) {
  overflow: visible !important;
}

/* Toolbar stripe look (the rest of toolbar cosmetics come from editor.scss) */
:deep(.vuetify-pro-tiptap-editor__toolbar) {
  background: #ffffff;                 /* solid so it doesn't show content through */
  border-bottom: 1px solid #e5e7eb;    /* subtle divider under toolbar */
}

/* Solid, rounded boundary for the whole editor card */
:deep(.vuetify-pro-tiptap-editor) {
  border: 1px solid #e5e7eb;          /* slate-200 */
  border-radius: 12px;
  background: #fff;
  overflow: visible;                   /* keep sticky working */
  position: relative;                  /* positioning context for sticky */
}

/* Sticky toolbar wrapper */
.tiptap-toolbar-sticky {
  position: sticky;
  top: var(--tiptap-toolbar-offset, 0px);
  z-index: 10;                         /* above content and bubble menu */
  background: #fff;                    /* avoid content showing through */
}

/* Give content comfortable padding like the demo */
:deep(.vuetify-pro-tiptap-editor__content .ProseMirror) {
  background: #fff;
  padding: 16px;            /* match GitHub base rhythm */
  min-height: 300px;
  border-radius: 0;         /* sharp corners like the demo */
  border-top: 1px solid #e5e7eb; /* subtle divider under toolbar */
  outline: none;            /* no focus halo */
}

</style>
